import { StrictMode, type ReactNode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import './styles.css';
import { TermsModal } from './terms-modal';

type CapabilityIconName = 'plan' | 'monitor' | 'evaluate' | 'improve' | 'report' | 'start';

const capabilities: { icon: CapabilityIconName; title: string; body: string }[] = [
  {
    icon: 'plan',
    title: 'Plan with confidence',
    body: 'Quality objectives, stakeholder advice, effective indicators, measurable targets, and an achievable approach with scheduled activities.',
  },
  {
    icon: 'monitor',
    title: 'Monitor daily, weekly, monthly',
    body: "Ongoing checks, stakeholders' feedback ensure processes and activities are implemented as planned, addressing issues arising.",
  },
  {
    icon: 'evaluate',
    title: 'Evaluate quality achieved',
    body: 'Internal audits and periodic reviews confirm operations are effective, achieving intended outcomes.',
  },
  {
    icon: 'improve',
    title: 'Improve ongoing',
    body: 'Insights at every stage drive practical, ongoing improvements.',
  },
  {
    icon: 'report',
    title: 'Visible, data-driven',
    body: 'Verified progress and evidence-based decision making with clear, robust reporting.',
  },
  {
    icon: 'start',
    title: 'Get started!',
    body: 'Bring your quality approach together and start with confidence.',
  },
];

function CapabilityIcon({ name }: { name: CapabilityIconName }) {
  const paths = {
    plan: (
      <>
        <path d="M8 4h8v3H8z" />
        <path d="M6 6H4v14h16V6h-2" />
        <path d="m8 12 2 2 4-4M8 17h7" />
      </>
    ),
    monitor: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4l3 2" />
      </>
    ),
    evaluate: (
      <>
        <circle cx="10" cy="10" r="5" />
        <path d="m14 14 5 5M8 10l1.5 1.5L12 8" />
      </>
    ),
    improve: <path d="m4 17 5-5 4 3 7-8M15 7h5v5" />,
    report: (
      <>
        <path d="M4 20V11M10 20V7M16 20V4M22 20H2" />
      </>
    ),
    start: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="m10 8 6 4-6 4z" />
      </>
    ),
  } satisfies Record<CapabilityIconName, ReactNode>;

  return (
    <svg aria-hidden="true" className="capability-icon" fill="none" viewBox="0 0 24 24">
      {paths[name]}
    </svg>
  );
}

function App() {
  const config = window.QUALITY_VET_PUBLIC_CONFIG;
  const [termsOpen, setTermsOpen] = useState(false);
  const appUrl = config?.appUrl
    ? new URL(config.appUrl, window.location.origin).toString()
    : 'https://app.quality.vet';

  useEffect(() => {
    document.title = 'quality.vet | Quality operations';
  }, []);

  return (
    <div className="site-shell" data-auth-contract="quality-vet-public-auth-link-v1">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="site-header">
        <a className="brand" href="#main-content" aria-label="quality.vet home">
          <img className="brand-logo" src="/quality-vet-mark.png" alt="" />
        </a>
        <nav aria-label="Main navigation">
          <a className="button button-small" href={appUrl}>
            Login
          </a>
        </nav>
      </header>

      <main id="main-content" tabIndex={-1}>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Built for Australian VET</p>
            <h1>Set, deliver and prove quality operations</h1>
            <p className="lead">
              Bring systematic planning, monitoring and evaluation together. Stakeholder advice
              driving quality - stakeholder feedback proving quality - all while leading ongoing
              improvements.
            </p>
          </div>
          <section className="signal-card" aria-labelledby="quality-approach-title">
            <h2 id="quality-approach-title">Quality Approach</h2>
            <div className="quality-approach-grid">
              <div className="quality-approach-level">
                <span aria-hidden="true">→</span> Plan
              </div>
              <div className="quality-approach-level">
                <span aria-hidden="true">→</span> Monitor and Evaluate
              </div>
              <div className="quality-approach-level">
                <span aria-hidden="true">→</span> Improve and Report
              </div>
            </div>
          </section>
        </section>

        <section className="capabilities" id="platform">
          <p className="eyebrow">Quality by design</p>
          <h2>
            Embedding stakeholder expectations into a defensible approach, delivering demonstrable
            quality outcomes.
          </h2>
          <p>
            Plan out the organisation's quality approach and guide what is monitored and evaluated.
            Operational systems perform monitoring and produce evidence. quality.vet provides
            oversight of that monitoring and uses the resulting evidence to support evaluation,
            organisational assurance and improvement.
          </p>
          <div className="capability-grid">
            {capabilities.map((capability) => (
              <article key={capability.title}>
                <CapabilityIcon name={capability.icon} />
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="contact-panel">
          <div>
            <p className="eyebrow">Get in touch</p>
            <h2>Know what is working. Improve what is not.</h2>
          </div>
          <a className="button button-light" href="mailto:info@quality.vet">
            info@quality.vet
          </a>
        </section>
      </main>

      <footer>
        <a className="brand footer-brand" href="#main-content" aria-label="Back to main content">
          <span className="footer-wordmark">
            quality<span>.vet</span>
          </span>
        </a>
        <p>
          Systematic monitoring and evaluation for Australian vocational education and training.
        </p>
      </footer>
      <button
        aria-label="Service Terms"
        aria-controls="service-terms-dialog"
        aria-expanded={termsOpen}
        aria-haspopup="dialog"
        className="service-terms-trigger"
        title="Service Terms"
        type="button"
        onClick={() => setTermsOpen(true)}
      >
        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8.25" />
          <path d="M12 10.75v5.5" />
          <circle className="service-terms-trigger-dot" cx="12" cy="7.75" r="0.75" />
        </svg>
      </button>
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
